import { ActionDetailsWrapper } from "@app/components/actions/ActionDetailsWrapper";
import type { ToolExecutionDetailsProps } from "@app/components/actions/mcp/details/types";
import {
  Card,
  ContentMessage,
  Icon,
  Lightbulb04,
  Markdown,
} from "@dust-tt/sparkle";
import type {
  CallToolResult,
  TextContent,
} from "@modelcontextprotocol/sdk/types.js";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export function MCPAgentMemoryRetrieveActionDetails({
  toolOutput,
  displayContext,
}: ToolExecutionDetailsProps) {
  const { t } = useTranslation();
  const parsedMemories = useMemo(
    () => parseMemoriesFromOutput(toolOutput),
    [toolOutput]
  );

  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={t(
        "assistant.mcpRetrieveAgentMemory",
        "Retrieve Agent Memory"
      )}
      visual={Lightbulb04}
    >
      <div className="flex flex-col pt-4">
        <div className="flex flex-col gap-2">
          <span className="heading-base">
            {t("assistant.mcpSavedMemories", "Saved memories")}
          </span>
          {parsedMemories.length === 0 ? (
            <ActionCard
              actionText={t(
                "assistant.mcpNoMemoryRetrieved",
                "*No memory was retrieved.*"
              )}
            />
          ) : (
            <MemoriesCardList memories={parsedMemories} />
          )}
        </div>
      </div>
    </ActionDetailsWrapper>
  );
}

export function MCPAgentMemoryRecordActionDetails({
  toolParams,
  displayContext,
}: ToolExecutionDetailsProps) {
  const { t } = useTranslation();
  const entries = Array.isArray(toolParams.entries) ? toolParams.entries : [];
  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={t("assistant.mcpRecordAgentMemory", "Record Agent Memory")}
      visual={Lightbulb04}
    >
      <div className="flex flex-col pt-4">
        <div className="flex flex-col gap-2">
          <span className="heading-base">
            {entries.length === 1
              ? t("assistant.mcpRecordedMemory", "Recorded memory")
              : t("assistant.mcpRecordedMemories", "Recorded memories")}
          </span>
          {entries.length === 0 ? (
            <ActionCard
              actionText={t(
                "assistant.mcpNoEntriesRecorded",
                "*No entries were recorded.*"
              )}
            />
          ) : (
            <MemoriesCardList memories={entries} />
          )}
        </div>
      </div>
    </ActionDetailsWrapper>
  );
}

export function MCPAgentMemoryEditActionDetails({
  toolOutput,
  displayContext,
  toolName,
}: ToolExecutionDetailsProps & { toolName: string }) {
  const { t } = useTranslation();
  const updatedMemories = useMemo(
    () => parseMemoriesFromOutput(toolOutput),
    [toolOutput]
  );
  const toolNameText =
    toolName === "compact_memory"
      ? t("assistant.mcpCompactAgentMemory", "Compact Agent Memory")
      : t("assistant.mcpEditAgentMemory", "Edit Agent Memory");
  const subTitleText =
    toolName === "compact_memory"
      ? t("assistant.mcpCompactedMemories", "Compacted memories")
      : t("assistant.mcpEditedMemories", "Edited memories");
  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={toolNameText}
      visual={Lightbulb04}
    >
      <div className="flex flex-col gap-4 pt-4">
        <div className="flex flex-col gap-2">
          <div className="heading-base">{subTitleText}</div>
          {updatedMemories.length === 0 ? (
            <ActionCard
              actionText={t(
                "assistant.mcpNoMemoriesRemaining",
                "*No memories remaining.*"
              )}
            />
          ) : (
            <MemoriesCardList memories={updatedMemories} />
          )}
        </div>
      </div>
    </ActionDetailsWrapper>
  );
}

export function MCPAgentMemoryEraseActionDetails({
  toolParams,
  toolOutput,
  displayContext,
}: ToolExecutionDetailsProps) {
  const { t } = useTranslation();
  const indexes = Array.isArray(toolParams.indexes) ? toolParams.indexes : [];
  const remainingMemories = useMemo(
    () => parseMemoriesFromOutput(toolOutput),
    [toolOutput]
  );
  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={t("assistant.mcpEraseAgentMemory", "Erase Agent Memory")}
      visual={Lightbulb04}
    >
      <div className="flex flex-col gap-4 pt-4">
        <div className="flex flex-col gap-2">
          <span className="heading-base">
            {t("assistant.mcpOutputLabel", "Output")}
          </span>
          <ActionCard
            actionText={
              indexes.length === 0
                ? t(
                    "assistant.mcpNoMemoryEntriesErased",
                    "*No memory entries were erased.*"
                  )
                : t("assistant.mcpErasedMemoriesCount", {
                    defaultValue: "*Erased {{count}} {{memoryWord}}.*",
                    count: indexes.length,
                    memoryWord:
                      indexes.length === 1
                        ? t("assistant.mcpMemoryWordSingular", "memory")
                        : t("assistant.mcpMemoryWordPlural", "memories"),
                  })
            }
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="heading-base">
            {t("assistant.mcpRemainingMemories", "Remaining memories")}
          </div>
          {remainingMemories.length === 0 ? (
            <ActionCard
              actionText={t(
                "assistant.mcpNoMemoriesRemaining",
                "*No memories remaining.*"
              )}
            />
          ) : (
            <MemoriesCardList memories={remainingMemories} />
          )}
        </div>
      </div>
    </ActionDetailsWrapper>
  );
}

/**
 * Shared components & utils.
 */

const MemoriesCardList = ({ memories }: { memories: string[] }) => {
  return (
    <div className="flex flex-col gap-1">
      {memories.map((memory, index) => (
        <Card key={index} size="md" className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Icon visual={Lightbulb04} size="xs" />
            <div className="text-sm">{memory}</div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const ActionCard = ({ actionText }: { actionText: string }) => {
  return (
    <ContentMessage variant="primary" size="lg">
      <Markdown content={actionText} forcedTextSize="text-sm" />
    </ContentMessage>
  );
};

function parseMemoriesFromOutput(
  toolOutput: CallToolResult["content"] | null
): string[] {
  const memoryOutputs =
    toolOutput?.filter(
      (output): output is TextContent => output.type === "text"
    ) ?? [];

  const memories: string[] = [];

  memoryOutputs.forEach((output) => {
    if (output.text === "(memory empty)") {
      return;
    }
    const lines = output.text.split("\n");
    lines.forEach((line: string) => {
      const match = line.match(/^\[\d+\]\s*(.+)$/);
      if (match) {
        memories.push(match[1].trim());
      } else if (line.trim()) {
        memories.push(line.trim());
      }
    });
  });

  return memories;
}
